import { Navigate, useNavigate } from "react-router-dom";
import { t } from "ttag";

import { ScreenLayout } from "@/app/components/layouts";
import { ROUTES } from "@/app/router";
import { usePortalUserStore } from "@/app/stores";
import {
  triggerCustomerIoShopPaymentCompleted,
  triggerCustomerIoShopPaymentFailed,
} from "@/features/analytics/triggers";
import { useCreatePatientSubscriptions } from "@/features/billing/hooks";
import { useShopContext } from "@/features/products/context";
import { usePayWithKlarna } from "@/features/products/hooks";
import { SHOP_ROUTES } from "@/features/products/router";
import {
  PaymentSuccessModal,
  ShopProductsSummary,
  ShopSummaryHeader,
} from "@/features/products/sections";
import { LogoLoader } from "@/shared/components";
import { PAYMENT_METHOD_TYPE, SUBSCRIPTION_STATUS } from "@/shared/domains";
import type { Subscription } from "@/shared/domains";
import { useBoolean } from "@/shared/hooks";
import { Button, errorToast } from "@/shared/ui";

export const ShopSummaryScreen = () => {
  const user = usePortalUserStore((state) => state.portalUser);

  const {
    value: showSuccessfulPaymentModal,
    setTrue: openSuccessfulPaymentModal,
  } = useBoolean(false);

  const navigate = useNavigate();

  const {
    selectedProduct,
    selectedPrice,
    selectedCreditAmount,
    selectedPaymentMethodType,
    actions: { removeProduct, removeCreditAmount },
  } = useShopContext();

  const createPatientSubscriptionsMutation = useCreatePatientSubscriptions();

  const payWithKlarnaMutation = usePayWithKlarna({
    price: selectedPrice,
    referralCredit: selectedCreditAmount,
  });

  const createdSubscription =
    createPatientSubscriptionsMutation.data?.[0] ?? payWithKlarnaMutation.data;

  if (!selectedProduct) {
    return <Navigate to={SHOP_ROUTES.BASE} />;
  }

  const hasUnsuccessfulPayments = (subscriptions: Subscription[]) => {
    return subscriptions.some(
      (subscription) =>
        subscription?.status === SUBSCRIPTION_STATUS.INCOMPLETE ||
        subscription?.status === SUBSCRIPTION_STATUS.PAYMENT_FAILED,
    );
  };

  const handlePay = () => {
    if (!selectedProduct || !selectedPrice || !selectedPaymentMethodType) {
      return;
    }

    const totalPrice = selectedPrice.discount?.isValid
      ? selectedPrice.discount.priceAfterDiscount
      : selectedPrice.price;

    if (selectedPaymentMethodType === PAYMENT_METHOD_TYPE.KLARNA) {
      return payWithKlarnaMutation.mutate();
    }

    if (selectedPaymentMethodType === PAYMENT_METHOD_TYPE.CARD) {
      return createPatientSubscriptionsMutation.mutate(
        {
          subscriptions: [selectedPrice.id],
          couponCode: selectedPrice.discount?.code,
          referralCredit: selectedCreditAmount,
        },
        {
          onSuccess: (createdSubscriptions) => {
            if (user && !hasUnsuccessfulPayments(createdSubscriptions)) {
              triggerCustomerIoShopPaymentCompleted({
                user,
                products: [
                  {
                    productCategory:
                      createdSubscriptions[0]?.currentProduct.category.slug,
                    productName: createdSubscriptions[0]?.currentProduct.slug,
                    productId: selectedPrice.subscriptionPriceId,
                    price: totalPrice,
                    productFrequency: selectedPrice.durationInMonths,
                  },
                ],
                totalPrice,
                discount: selectedPrice.discount,
                succeededSubscriptionIds: [
                  String(createdSubscriptions[0]?.id ?? ""),
                ],
              });
            }
          },
          onSettled: (createdSubscriptions, error) => {
            if (
              error ||
              !createdSubscriptions ||
              hasUnsuccessfulPayments(createdSubscriptions)
            ) {
              if (user) {
                triggerCustomerIoShopPaymentFailed({
                  user,
                  productId: selectedProduct.id,
                  totalPrice,
                  discount: selectedPrice.discount,
                });
              }

              errorToast(
                t`We couldn't process your payment. Please check your payment method and try again. If the issue continues, please contact support.`,
              );
              return navigate(ROUTES.BILLING.OVERVIEW);
            }

            openSuccessfulPaymentModal();
          },
        },
      );
    }
  };

  const handleCloseModal = () => {
    navigate(ROUTES.SHOP.OVERVIEW);
  };

  const handleBack = () => {
    removeProduct();
    removeCreditAmount();
    navigate(ROUTES.SHOP.OVERVIEW);
  };

  return (
    <>
      <LogoLoader
        show={createPatientSubscriptionsMutation.isPending}
        fullscreen
      >
        {t`We are processing your payment!`}
      </LogoLoader>

      <LogoLoader show={payWithKlarnaMutation.isPending} fullscreen>
        {t`Almost there — we're redirecting you to Klarna to complete your payment`}
      </LogoLoader>

      <ScreenLayout>
        <div className="flex grow flex-col gap-11">
          <ShopSummaryHeader />

          <ShopProductsSummary />
        </div>

        <div className="flex flex-col-reverse gap-4 md:flex-row md:justify-between md:[&_*]:w-32">
          <Button onClick={handleBack} size="lg" variant="secondary">
            {t`Back`}
          </Button>

          <Button
            size="lg"
            disabled={!selectedPrice || !selectedPaymentMethodType}
            onClick={handlePay}
          >
            {t`Pay`}
          </Button>
        </div>

        {createdSubscription && (
          <PaymentSuccessModal
            show={showSuccessfulPaymentModal || Boolean(createdSubscription)}
            onClose={handleCloseModal}
            subscription={createdSubscription}
          />
        )}
      </ScreenLayout>
    </>
  );
};
